import { useEffect, useState } from "react";
import { api, formatZAR } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function Invoices() {
  const [list, setList] = useState([]);
  const load = () => api.get("/invoices").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const togglePaid = async (inv) => {
    const next = inv.status === "paid" ? "unpaid" : "paid";
    try {
      await api.patch(`/invoices/${inv.id}`, { status: next });
      toast.success(`Marked ${next}`);
      load();
    } catch {
      toast.error("Failed to update");
    }
  };

  const unpaidTotal = list.filter((i) => i.status === "unpaid").reduce((s, i) => s + i.total, 0);
  const paidTotal = list.filter((i) => i.status === "paid").reduce((s, i) => s + i.total, 0);

  return (
    <div className="p-10 max-w-7xl mx-auto" data-testid="admin-invoices">
      <div className="mb-10">
        <div className="text-overline text-zinc-500">Invoices</div>
        <h1 className="font-serif text-4xl lg:text-5xl font-medium tracking-tight mt-2">Billing</h1>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-10">
        <Stat label="Total" value={list.length} />
        <Stat label="Outstanding" value={formatZAR(unpaidTotal)} accent />
        <Stat label="Collected" value={formatZAR(paidTotal)} />
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-zinc-200 bg-zinc-50">
            <tr className="text-left">
              <Th>Invoice #</Th><Th>Matter</Th><Th>Items</Th><Th>Total</Th><Th>Status</Th><Th>Created</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500">No invoices yet.</td></tr>
            ) : (
              list.map((inv) => (
                <tr key={inv.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`invoice-${inv.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{inv.invoice_number}</td>
                  <td className="px-4 py-3">
                    <Link to={`/admin/matters/${inv.matter_id}`} className="hover:underline">{inv.client_name || "—"}</Link>
                  </td>
                  <td className="px-4 py-3 text-zinc-600">{inv.items.length}</td>
                  <td className="px-4 py-3 font-medium">{formatZAR(inv.total)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] tracking-widest uppercase px-2 py-1 ${inv.status === "paid" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-zinc-500">{new Date(inv.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => togglePaid(inv)}
                      className="text-xs text-zinc-600 hover:text-zinc-900 underline"
                      data-testid={`toggle-paid-${inv.id}`}
                    >
                      Mark {inv.status === "paid" ? "unpaid" : "paid"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 text-overline text-zinc-500 font-normal">{children}</th>;
const Stat = ({ label, value, accent }) => (
  <div className={`p-6 border border-zinc-200 ${accent ? "bg-zinc-900 text-white" : "bg-white"}`}>
    <div className={`text-overline ${accent ? "text-zinc-400" : "text-zinc-500"}`}>{label}</div>
    <div className="font-serif text-4xl font-medium mt-2">{value}</div>
  </div>
);
